import { useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { Button } from '../../ui/button';
import { CheckCircle, X, Tag, Send } from 'lucide-react';
import { motion } from 'motion/react';
import type { ActivitySlideProps } from '../types';
import { getDndBackend } from './dndBackend';
import { stripHtml } from '../../../../utils/slides/stripHtml';

interface Label {
  id: string;
  text: string;
  correctPosition: { x: number; y: number };
}

interface PlacedLabel {
  labelId: string;
  position: { x: number; y: number };
}

function DraggableLabel({ label, isPlaced, disabled }: { label: Label; isPlaced: boolean; disabled: boolean }) {
  const [{ isDragging }, drag] = useDrag({
    type: 'LABEL',
    item: { labelId: label.id },
    canDrag: !isPlaced && !disabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <motion.div
      ref={drag as unknown as React.Ref<HTMLDivElement>}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={!isPlaced && !disabled ? { scale: 1.05 } : undefined}
      whileTap={!isPlaced && !disabled ? { scale: 0.95 } : undefined}
      className={`px-3 py-2 rounded-xl font-medium text-sm transition-all touch-none ${
        isPlaced || disabled
          ? 'bg-muted/50 text-muted-foreground cursor-not-allowed line-through opacity-50'
          : isDragging
          ? 'opacity-40 bg-primary text-white scale-90'
          : 'bg-gradient-to-r from-primary to-indigo-600 text-white hover:shadow-lg cursor-move'
      }`}
      role="button"
      tabIndex={0}
    >
      {label.text}
    </motion.div>
  );
}

function DropZone({
  position,
  onDrop,
  placedLabel,
  isCorrect,
  disabled,
}: {
  position: { x: number; y: number };
  onDrop: (labelId: string) => void;
  placedLabel?: PlacedLabel;
  isCorrect?: boolean;
  disabled?: boolean;
}) {
  const [{ isOver }, drop] = useDrop({
    accept: 'LABEL',
    drop: (item: { labelId: string }) => {
      if (!disabled) onDrop(item.labelId);
    },
    canDrop: () => !disabled,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div
      ref={drop as unknown as React.Ref<HTMLDivElement>}
      className="absolute"
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        transform: 'translate(-50%, -50%)',
      }}
    >
      <div
        className={`w-3 h-3 md:w-4 md:h-4 rounded-full transition-all ${
          placedLabel
            ? isCorrect
              ? 'bg-accent ring-4 ring-accent/20'
              : 'bg-destructive ring-4 ring-destructive/20'
            : isOver
            ? 'bg-primary ring-4 ring-primary/20'
            : 'bg-muted-foreground ring-4 ring-muted-foreground/20'
        }`}
      />
      {placedLabel && (
        <motion.div
          initial={{ scale: 0, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          className={`absolute top-4 md:top-6 left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-1 md:px-3 md:py-1.5 rounded-lg text-xs md:text-sm font-medium shadow-lg ${
            isCorrect
              ? 'bg-accent text-white'
              : 'bg-destructive text-white'
          }`}
        >
          {placedLabel.labelId}
        </motion.div>
      )}
    </div>
  );
}

export default function LabelDiagram({
  slide,
  onAnswerChange,
  hasSubmitted,
  className = '',
}: ActivitySlideProps) {
  const [placedLabels, setPlacedLabels] = useState<PlacedLabel[]>([]);
  const [showResults, setShowResults] = useState(false);
  const { backend, options } = getDndBackend();

  const labels: Label[] = [
    { id: 'Chloroplast', text: 'Chloroplast', correctPosition: { x: 35, y: 40 } },
    { id: 'Cell Wall', text: 'Cell Wall', correctPosition: { x: 15, y: 30 } },
    { id: 'Nucleus', text: 'Nucleus', correctPosition: { x: 50, y: 50 } },
    { id: 'Vacuole', text: 'Vacuole', correctPosition: { x: 65, y: 55 } },
    { id: 'Mitochondria', text: 'Mitochondria', correctPosition: { x: 40, y: 65 } },
    { id: 'Cell Membrane', text: 'Cell Membrane', correctPosition: { x: 20, y: 50 } },
  ];

  const handleDrop = (position: { x: number; y: number }, labelId: string) => {
    // Remove if already placed
    const filtered = placedLabels.filter(pl => pl.labelId !== labelId);
    const updated = [...filtered, { labelId, position }];
    setPlacedLabels(updated);
    onAnswerChange(updated);
  };

  const checkAnswers = () => {
    setShowResults(true);
  };

  const isLabelPlaced = (labelId: string) => {
    return placedLabels.some(pl => pl.labelId === labelId);
  };

  const isCorrectPlacement = (labelId: string, position: { x: number; y: number }) => {
    const label = labels.find(l => l.id === labelId);
    if (!label) return false;
    
    const distance = Math.sqrt(
      Math.pow(label.correctPosition.x - position.x, 2) +
      Math.pow(label.correctPosition.y - position.y, 2)
    );
    
    return distance < 5; // Within 5% tolerance
  };

  const correctCount = placedLabels.filter(pl =>
    isCorrectPlacement(pl.labelId, pl.position)
  ).length;

  return (
    <DndProvider backend={backend} options={options}>
      <div className={`w-full ${className}`}>
        <div className="bg-gradient-to-br from-teal-500/5 via-green-500/5 to-blue-500/5 rounded-3xl p-5 sm:p-6 border border-border/40">
          {/* Header */}
          <div className="text-center mb-4">
            <div className="inline-flex items-center gap-2 bg-teal-500/10 text-teal-600 px-4 py-1.5 rounded-full mb-2">
              <Tag className="w-4 h-4" />
              <span className="text-sm font-bold">Label the Diagram</span>
            </div>
            {slide.content?.showTitle !== false && slide.content?.title && slide.content.title !== 'Label the Diagram' && (
              <h2 className="text-lg font-bold mb-1">{stripHtml(slide.content.title)}</h2>
            )}
            <p className="text-xs text-muted-foreground">
              {slide.content?.instructions || 'Drag labels to the correct positions'}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Labels Panel */}
            <div className="lg:col-span-1 order-2 lg:order-1">
              <div className="bg-card/60 backdrop-blur-sm rounded-2xl p-4 border border-border/40 lg:sticky lg:top-4">
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-3">Available Labels</h3>
                <div className="space-y-2">
                  {labels.map(label => (
                    <DraggableLabel
                      key={label.id}
                      label={label}
                      isPlaced={isLabelPlaced(label.id)}
                      disabled={Boolean(hasSubmitted)}
                    />
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-border/30">
                  <div className="space-y-2 mb-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Placed</span>
                      <span className="font-bold">
                        {placedLabels.length} / {labels.length}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      {labels.map((l, i) => (
                        <div key={l.id} className={`flex-1 h-1.5 rounded-full transition-all ${
                          isLabelPlaced(l.id) ? 'bg-teal-500' : 'bg-muted-foreground/20'
                        }`} />
                      ))}
                    </div>
                    {showResults && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Correct</span>
                        <span className="font-bold text-accent">
                          {correctCount} / {labels.length}
                        </span>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={checkAnswers}
                    disabled={Boolean(hasSubmitted) || placedLabels.length !== labels.length}
                    className="w-full rounded-xl bg-gradient-to-r from-teal-600 to-green-600 hover:from-teal-700 hover:to-green-700 text-white"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {showResults ? 'Results Shown' : 'Check Answers'}
                  </Button>

                  {showResults && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setPlacedLabels([]);
                        setShowResults(false);
                        onAnswerChange([]);
                      }}
                      className="w-full rounded-xl mt-2 border-border/40"
                      disabled={Boolean(hasSubmitted)}
                    >
                      Try Again
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Diagram Area */}
            <div className="lg:col-span-3 order-1 lg:order-2">
              <div className="bg-white rounded-2xl shadow-lg border-2 border-border/30 p-3">
                <div className="relative bg-gradient-to-br from-green-50 to-blue-50 rounded-xl overflow-hidden aspect-video">
                  {/* Simplified Plant Cell Illustration */}
                  <svg
                    viewBox="0 0 800 450"
                    className="w-full h-full"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {/* Cell Wall (outer) */}
                    <rect
                      x="100"
                      y="75"
                      width="600"
                      height="300"
                      rx="20"
                      fill="none"
                      stroke="#10B981"
                      strokeWidth="8"
                    />
                    
                    {/* Cell Membrane (inner) */}
                    <rect
                      x="120"
                      y="95"
                      width="560"
                      height="260"
                      rx="15"
                      fill="none"
                      stroke="#6366F1"
                      strokeWidth="4"
                      strokeDasharray="10,5"
                    />

                    {/* Nucleus */}
                    <circle cx="400" cy="225" r="60" fill="#8B5CF6" opacity="0.3" stroke="#8B5CF6" strokeWidth="3" />
                    <circle cx="400" cy="225" r="40" fill="#8B5CF6" opacity="0.5" />

                    {/* Chloroplasts */}
                    <ellipse cx="280" cy="180" rx="30" ry="20" fill="#10B981" opacity="0.4" />
                    <ellipse cx="240" cy="250" rx="25" ry="18" fill="#10B981" opacity="0.4" />
                    <ellipse cx="550" cy="200" rx="28" ry="20" fill="#10B981" opacity="0.4" />

                    {/* Vacuole */}
                    <ellipse cx="520" cy="248" rx="80" ry="60" fill="#60A5FA" opacity="0.2" stroke="#60A5FA" strokeWidth="2" />

                    {/* Mitochondria */}
                    <ellipse cx="320" cy="293" rx="35" ry="15" fill="#F59E0B" opacity="0.4" />
                    <ellipse cx="480" cy="310" rx="30" ry="14" fill="#F59E0B" opacity="0.4" />
                  </svg>

                  {/* Drop Zones */}
                  {labels.map(label => {
                    const placedLabel = placedLabels.find(pl => pl.labelId === label.id);
                    const position = placedLabel?.position || label.correctPosition;
                    
                    return (
                      <DropZone
                        key={label.id}
                        position={position}
                        onDrop={(labelId) => handleDrop(label.correctPosition, labelId)}
                        placedLabel={placedLabel}
                        disabled={Boolean(hasSubmitted)}
                        isCorrect={
                          showResults && placedLabel
                            ? isCorrectPlacement(placedLabel.labelId, placedLabel.position)
                            : undefined
                        }
                      />
                    );
                  })}
                </div>

                {/* Results Summary */}
                {showResults && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    className={`mt-4 p-4 rounded-xl text-center border ${
                      correctCount === labels.length
                        ? 'bg-accent/10 border-accent/20'
                        : 'bg-amber-500/10 border-amber-500/20'
                    }`}
                  >
                    <p className={`font-bold text-base ${
                      correctCount === labels.length ? 'text-accent' : 'text-amber-600'
                    }`}>
                      {correctCount === labels.length
                        ? '✅ Perfect! All labels placed correctly!'
                        : `${correctCount} of ${labels.length} correct — ${Math.round((correctCount / labels.length) * 100)}%`}
                    </p>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DndProvider>
  );
}
